import api, { handleAuthExpiration } from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface Branding {
  id: string;
  name: string;
  type: string;
  publicLogoPath: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Public logo URL for img src (paths are served via /uploads proxy). */
export function resolveLogoSrc(publicLogoPath: string | null): string | null {
  if (!publicLogoPath) return null;
  return publicLogoPath;
}

/** Branding for the logged-in user's organisation (dashboard header). */
export function resolveUserOrganisationBrandingId(
  organisationId: string | null | undefined,
  organisation: { brandingId: string | null } | null | undefined,
): string | null {
  if (!organisationId) return null;
  return organisation?.brandingId ?? null;
}

/**
 * Branding for a site control section.
 * - Site belongs to an organisation with branding: use the organisation's branding.
 * - Otherwise: use site-level branding (including when the org has no branding).
 */
export function resolveSiteBrandingId(
  site: { brandingId?: string | null; organisationId?: string | null } | null | undefined,
  organisation: { brandingId: string | null } | null | undefined,
): string | null {
  if (!site) return null;

  if (site.organisationId && organisation?.brandingId) {
    return organisation.brandingId;
  }

  return site.brandingId ?? null;
}

async function postBrandingLogo(brandingId: string, file: File): Promise<Branding> {
  const formData = new FormData();
  formData.append('logo', file);

  const base = API_BASE_URL.replace(/\/$/, '');
  const token = localStorage.getItem('accessToken');
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${base}/branding/${encodeURIComponent(brandingId)}/logo`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (res.status === 401) {
    if (
      localStorage.getItem('accessToken') &&
      !window.location.pathname.includes('/login')
    ) {
      handleAuthExpiration();
    }
    throw Object.assign(new Error('Unauthorized'), {
      response: { status: 401, data: { error: 'Unauthorized' } },
    });
  }

  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errMsg =
      typeof (data as { error?: string }).error === 'string'
        ? (data as { error: string }).error
        : 'Failed to upload logo';
    throw Object.assign(new Error(errMsg), {
      response: { status: res.status, data },
    });
  }

  return data as Branding;
}

export const brandingApi = {
  getAll: async (): Promise<Branding[]> => {
    const { data } = await api.get<Branding[]>('/branding');
    return data;
  },

  getById: async (id: string): Promise<Branding> => {
    const { data } = await api.get<Branding>(`/branding/${id}`);
    return data;
  },

  /**
   * Create with JSON, then upload logo in a second multipart request.
   * Avoids mixing multipart and Zod on a single route (a common source of "Validation error").
   */
  create: async (payload: { name: string; logoFile?: File | null }): Promise<Branding> => {
    const name = payload.name.trim();
    const { data } = await api.post<Branding>('/branding', { name });

    if (!payload.logoFile) {
      return data;
    }

    try {
      return await postBrandingLogo(data.id, payload.logoFile);
    } catch (err) {
      await api.delete(`/branding/${data.id}`).catch(() => undefined);
      throw err;
    }
  },

  update: async (
    id: string,
    payload: { name: string; logoFile?: File | null },
  ): Promise<Branding> => {
    const name = payload.name.trim();
    const { data } = await api.put<Branding>(`/branding/${id}`, { name });

    if (!payload.logoFile) {
      return data;
    }

    return postBrandingLogo(id, payload.logoFile);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/branding/${id}`);
  },
};
